/**
 * Alta e ingreso del comprador al portal (#46).
 *
 * Los dos endpoints devuelven **404** si la marca es farmacéutica o tiene el
 * portal apagado, no 403: para esa marca el portal no existe. Este formulario
 * ni siquiera se monta en ese caso —`PortalPage` no llega hasta acá—, pero el
 * servidor es el que manda y responde lo mismo si alguien lo llama por fuera.
 *
 * El alta guarda el consentimiento junto con la **versión del texto de
 * privacidad** (`notice_version`). Sin esa versión no se puede demostrar a
 * qué dio su consentimiento la persona, que es la mitad del requisito legal;
 * por eso el texto está a la vista del formulario y no en una página aparte.
 */
import { useMutation } from '@tanstack/react-query';
import { useState, type FormEvent, type ReactNode } from 'react';

import type { ApiError } from '@/api/errors';
import { useI18n } from '@/i18n/useI18n';
import { Button } from '@/ui/Button';
import { Card, CardBody, CardHeader } from '@/ui/Card';
import { ErrorState } from '@/ui/states';
import {
  NOTICE_VERSION,
  portalLogin,
  portalRegister,
  type PortalSession,
} from './portalApi';

type Mode = 'login' | 'register';

export function PortalSignIn({
  tenant,
  onSession,
}: {
  tenant: string;
  onSession: (session: PortalSession) => void;
}): ReactNode {
  const { t, language } = useI18n();
  const [mode, setMode] = useState<Mode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');

  const submit = useMutation<PortalSession, ApiError>({
    mutationFn: () =>
      mode === 'login'
        ? portalLogin({ tenant, email, password })
        : portalRegister({
            tenant,
            email,
            password,
            locale: language,
            notice_version: NOTICE_VERSION,
            ...(displayName.trim() === '' ? {} : { display_name: displayName.trim() }),
          }),
    onSuccess: onSession,
  });

  const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    submit.mutate();
  };

  return (
    <Card>
      <CardHeader
        title={mode === 'login' ? t('portal.login.title') : t('portal.register.title')}
        actions={
          // Un solo botón que alterna, y no dos solapas: con dos, el nombre
          // accesible del botón que cambia de modo y el del que envía el
          // formulario serían el mismo, y quien navega por teclado o con
          // lector de pantalla no puede distinguirlos.
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setMode(mode === 'login' ? 'register' : 'login');
              submit.reset();
            }}
          >
            {mode === 'login' ? t('portal.register.title') : t('portal.login.title')}
          </Button>
        }
      />
      <CardBody>
        <form onSubmit={onSubmit} className="max-w-sm space-y-3">
          <label className="block text-sm">
            <span className="text-ink-muted">{t('portal.field.email')}</span>
            <input
              type="email"
              required
              autoComplete="off"
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
              }}
              className="mt-1 w-full rounded border border-border-strong bg-surface px-3 py-2 text-sm text-ink"
            />
          </label>

          <label className="block text-sm">
            <span className="text-ink-muted">{t('portal.field.password')}</span>
            <input
              type="password"
              required
              minLength={mode === 'register' ? 10 : 1}
              autoComplete="off"
              value={password}
              onChange={(event) => {
                setPassword(event.target.value);
              }}
              className="mt-1 w-full rounded border border-border-strong bg-surface px-3 py-2 text-sm text-ink"
            />
            {mode === 'register' && (
              <span className="mt-1 block text-xs text-ink-subtle">
                {t('portal.field.passwordHint')}
              </span>
            )}
          </label>

          {mode === 'register' && (
            <>
              <label className="block text-sm">
                <span className="text-ink-muted">{t('portal.field.name')}</span>
                <input
                  value={displayName}
                  onChange={(event) => {
                    setDisplayName(event.target.value);
                  }}
                  className="mt-1 w-full rounded border border-border-strong bg-surface px-3 py-2 text-sm text-ink"
                />
              </label>
              <p className="text-xs text-ink-subtle">
                {t('portal.notice', { version: NOTICE_VERSION })}
              </p>
            </>
          )}

          <Button type="submit" variant="primary" disabled={submit.isPending}>
            {mode === 'login' ? t('portal.login.submit') : t('portal.register.submit')}
          </Button>

          {submit.isError && <ErrorState error={submit.error} />}
        </form>
      </CardBody>
    </Card>
  );
}
